import Draggable from 'react-draggable';
import { noop } from 'lodash-es';
import React, { Component } from 'react';
import ReactModal from 'react-modal';

import { swallowClick, isWindows } from '../utils';
import * as Components from './components';
import { modalSizes } from './constants';

export const fjModalSizes = modalSizes;

const baseStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
};

const modalStyle = (color?: string | false): ReactModal.Styles => ({
  overlay: {
    ...baseStyle,
    backgroundColor: color || 'rgba(40, 40, 40, 0.25)',
    zIndex: 1000,
  },
  content: {
    ...baseStyle,
    pointerEvents: 'none',
    background: 'none',
    border: 0,
    borderRadius: 0,
    padding: 0,
  },
});

interface ModalDimensions {
  width?: number | string;
  height?: number | string;
}

interface ModalButtonConfig {
  text?: React.ReactNode;
  callback?: () => void;
  disabled?: boolean;
  // Any remaining keys are spread onto the underlying button component.
  [key: string]: unknown;
}

interface FjModalProps {
  isOpen?: boolean;
  onRequestClose?: () => void;
  size?: string;
  allowScroll?: boolean;
  isDraggable?: boolean;
  buttonFontSize?: number | string;
  showX?: boolean;
  onClickX?: () => void;
  overlayColor?: string;
  borderColor?: string;
  headerFontSize?: number;
  centerText?: boolean;
  centerContent?: boolean;
  borderless?: boolean;
  titleWrapper?: React.ElementType;
  windowsMode?: boolean;
  title?: React.ReactNode;
  children?: React.ReactNode;
  cancel?: ModalButtonConfig;
  accept?: ModalButtonConfig;
  importFile?: ModalButtonConfig;
  contentClassName?: string;
  className?: string;
  dataTestId?: string;
  blocking?: boolean;
  appElement?: HTMLElement;
  reactModalProps?: Partial<ReactModal.Props>;
  dimensions?: ModalDimensions;
}

// styled-components v6 .attrs() typings do not surface props injected via
// attrs (CloseIcon's `icon`), and FjButton forwards extra style props
// (`fontSize`) to the DOM that are not part of FjButtonProps. Re-type these
// styled components to match how the modal actually uses them (runtime unchanged).
const CloseIcon = Components.CloseIcon as unknown as React.ComponentType<{
  onClick?: () => void;
}>;

interface FooterButtonProps {
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  color?: string;
  fontSize?: number | string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

const CancelButton = Components.CancelButton as unknown as React.ComponentType<FooterButtonProps>;
const AcceptButton = Components.AcceptButton as unknown as React.ComponentType<FooterButtonProps>;
const ImportButton = Components.ImportButton as unknown as React.ComponentType<FooterButtonProps>;

class FjModal extends Component<FjModalProps> {
  static components = Components;

  static defaultProps: Partial<FjModalProps> = {
    isOpen: true,
    onRequestClose: noop,
    size: fjModalSizes.SM,
    allowScroll: false,
    isDraggable: false,
    buttonFontSize: 12,
    showX: false,
    onClickX: noop,
    overlayColor: 'rgba(48, 54, 55, 0.25)',
    borderColor: '#156777',
    headerFontSize: 14,
    centerText: false,
    centerContent: false,
    borderless: false,
    titleWrapper: (props: React.ComponentPropsWithoutRef<'span'>) => <span {...props} />,
  };

  draggableRef = React.createRef<HTMLDivElement>();

  isWindows = (): boolean =>
    typeof this.props.windowsMode !== 'undefined' ? this.props.windowsMode : isWindows();

  renderBody(): React.ReactNode {
    const {
      showX,
      onClickX,
      title,
      children,
      centerContent,
      borderless,
      allowScroll,
      titleWrapper: TitleWrapper = 'span',
    } = this.props;
    return (
      <Components.Body
        centerContent={centerContent}
        borderless={borderless}
        allowScroll={allowScroll}
      >
        {showX && <CloseIcon onClick={onClickX} />}
        {title && (
          <Components.Header centerContent={centerContent}>
            <TitleWrapper>{title}</TitleWrapper>
          </Components.Header>
        )}
        {children}
      </Components.Body>
    );
  }

  renderCancel(): React.ReactNode {
    const { cancel, borderColor, buttonFontSize } = this.props;
    if (!cancel) {
      return null;
    }

    const { disabled, callback, text, ...rest } = cancel;

    return (
      <CancelButton
        disabled={disabled}
        onClick={swallowClick(callback as () => void)}
        color={borderColor}
        fontSize={buttonFontSize}
        {...rest}
      >
        {text}
      </CancelButton>
    );
  }

  renderAccept(): React.ReactNode {
    const { accept, borderColor, buttonFontSize } = this.props;
    if (!accept) {
      return null;
    }

    const { disabled, callback, text, ...rest } = accept;

    return (
      <AcceptButton
        disabled={disabled}
        onClick={swallowClick(callback as () => void)}
        color={borderColor}
        fontSize={buttonFontSize}
        {...rest}
      >
        {text}
      </AcceptButton>
    );
  }

  renderImport(): React.ReactNode {
    const { importFile, borderColor, buttonFontSize } = this.props;
    if (!importFile) {
      return null;
    }

    const { disabled, callback, text, ...rest } = importFile;

    return (
      <ImportButton
        disabled={disabled}
        onClick={swallowClick(callback as () => void)}
        color={borderColor}
        fontSize={buttonFontSize}
        {...rest}
      >
        {text}
      </ImportButton>
    );
  }

  renderFooter(): React.ReactNode {
    const windows = this.isWindows();
    return (
      <Components.Footer windows={windows}>
        {/* Support Windows-style button ordering: */}
        {!windows && this.renderCancel()}
        {this.renderImport()}
        {this.renderAccept()}
        {windows && this.renderCancel()}
      </Components.Footer>
    );
  }

  renderDraggable(): React.ReactNode {
    const { allowScroll, contentClassName, isDraggable, dataTestId } = this.props;
    const content = (
      <Components.ContentWrapper
        ref={isDraggable ? this.draggableRef : undefined}
        allowScroll={allowScroll}
        className={contentClassName}
        draggable={isDraggable}
        data-testid={dataTestId}
      >
        {this.renderBody()}
        {this.renderFooter()}
      </Components.ContentWrapper>
    );
    if (isDraggable) {
      return (
        <Draggable
          nodeRef={this.draggableRef}
          // Clicks in interactive elements should NOT be treated as drag starts
          cancel="input, button, [role='button']"
        >
          {content}
        </Draggable>
      );
    }
    return content;
  }

  render(): React.ReactNode {
    const {
      size,
      isOpen,
      blocking,
      appElement,
      onRequestClose,
      reactModalProps,
      dimensions,
      className,
      overlayColor,
      centerText,
      dataTestId,
    } = this.props;
    return (
      <ReactModal
        className={className}
        style={modalStyle(blocking && overlayColor)}
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        appElement={appElement}
        ariaHideApp={!!appElement}
        {...reactModalProps}
      >
        <Components.InnerWrapper
          size={size}
          centerText={centerText}
          dimensions={dimensions}
          data-testid={dataTestId}
        >
          {this.renderDraggable()}
        </Components.InnerWrapper>
      </ReactModal>
    );
  }
}

export default FjModal;
